import {
  DEFAULT_LANGUAGE,
  type CatalogEntry,
  type ContentResponse,
  type LevelView,
  type LocalizedText,
  type MarkSeenRequest,
  type ProfileResponse,
  type SceneView,
} from '@bioforge/protocol';
import { ApiRequestError, type ApiClient } from '../api/api-client.js';

/**
 * Сюжет и знакомство с новым (SPEC.md, 15.7 и 19): что показать перед уровнем и после него,
 * и отметки «уже видел». Сервер только хранит отметки — решает клиент.
 */

/** Язык контента клиента; пока — только основной. */
export const LANGUAGE = DEFAULT_LANGUAGE;

/** Текст контента на языке клиента, иначе на основном. */
export function tr(text: LocalizedText): string {
  return text[LANGUAGE] ?? text.ru;
}

export type SeenKind = 'intro' | 'scene';

export interface LevelStory {
  /** Сцена перед уровнем (у боя с боссом — вступление), если игрок её ещё не видел. */
  readonly scene: SceneView | null;
  /** Новые для игрока элементы уровня — по одной карточке, в порядке данных уровня. */
  readonly cards: readonly CatalogEntry[];
}

export function beforeLevel(
  level: LevelView,
  content: ContentResponse,
  seen: SeenTracker,
): LevelStory {
  return {
    scene: unseenScene(level.sceneBefore, content, seen),
    cards: level.introduces
      .filter((id) => !seen.has('intro', id))
      .map((id) => content.catalog.find((entry) => entry.id === id))
      .filter((entry): entry is CatalogEntry => entry !== undefined),
  };
}

/** Сцена после победы, если игрок её ещё не видел. */
export function afterVictory(
  level: LevelView,
  content: ContentResponse,
  seen: SeenTracker,
): SceneView | null {
  return unseenScene(level.sceneAfter, content, seen);
}

function unseenScene(
  id: string | null,
  content: ContentResponse,
  seen: SeenTracker,
): SceneView | null {
  if (id === null || seen.has('scene', id)) return null;
  return content.scenes.find((scene) => scene.id === id) ?? null;
}

/**
 * Отметки «уже видел». Локально отметка действует сразу; на сервер уходит в фоне,
 * а при обрыве связи остаётся в очереди и отправляется со следующей отметкой.
 */
export class SeenTracker {
  private readonly marks: Record<SeenKind, Set<string>>;
  private readonly pending: Record<SeenKind, Set<string>> = { intro: new Set(), scene: new Set() };
  private sending: Promise<void> | null = null;

  constructor(
    seen: ProfileResponse['seen'],
    private readonly send: (request: MarkSeenRequest) => Promise<void>,
  ) {
    this.marks = { intro: new Set(seen.intros), scene: new Set(seen.scenes) };
  }

  has(kind: SeenKind, id: string): boolean {
    return this.marks[kind].has(id);
  }

  /** Просмотренное по порядку отметок. */
  list(kind: SeenKind): readonly string[] {
    return [...this.marks[kind]];
  }

  /** Отмечает просмотр; промис завершается после попытки отправки и не отклоняется. */
  mark(kind: SeenKind, ids: readonly string[]): Promise<void> {
    for (const id of ids) {
      if (this.marks[kind].has(id)) continue;
      this.marks[kind].add(id);
      this.pending[kind].add(id);
    }
    return this.flush();
  }

  /** Отправляет очередь; одновременно идёт не больше одного запроса. */
  async flush(): Promise<void> {
    while (this.sending) await this.sending;
    const intros = [...this.pending.intro];
    const scenes = [...this.pending.scene];
    if (intros.length === 0 && scenes.length === 0) return;
    this.sending = this.send({ intros, scenes }).then(
      () => {
        intros.forEach((id) => this.pending.intro.delete(id));
        scenes.forEach((id) => this.pending.scene.delete(id));
      },
      () => {
        // Нет связи — отметки остаются в очереди до следующей попытки.
      },
    );
    try {
      await this.sending;
    } finally {
      this.sending = null;
    }
  }
}

/** Трекер отметок аккаунта. Отклонённую сервером отметку повторно не отправляем. */
export function seenTracker(api: ApiClient, seen: ProfileResponse['seen']): SeenTracker {
  return new SeenTracker(seen, (request) =>
    api.markSeen(request).catch((error: unknown) => {
      if (!(error instanceof ApiRequestError)) throw error;
    }),
  );
}
